/**
 * Four named choices instead of a number field, per numeric param.
 *
 * Owner-stated: the inspector had a free-text box for font size, line height, padding
 * and border width, and *"refrain from having a lot of text inputs in the design"*.
 * Typing `17` into a font size is not a decision worth offering — the decision is
 * *bigger or smaller*, and four steps say that in one glance where a number needs
 * reading and comparing.
 *
 * Values are chosen per param rather than from one generic ramp: 16 is a normal font
 * size and a large gap, so a shared scale would be wrong at both ends. Everything here
 * is data, so a new component's param gets a sensible ramp by adding one entry.
 *
 * **A value outside its ramp is preserved, not snapped.** Existing templates and
 * hand-edited source hold arbitrary numbers, and quietly rounding one to the nearest
 * preset the first time its block was selected would edit an email nobody touched. The
 * widget shows the exact value as a fifth, transient option instead.
 */

export interface NumberPreset {
  value: number;
  /** Short label; `null` uses the number itself. */
  label: string | null;
}

/** Ramps keyed by param name, longest match first (`border_width` before `width`). */
const RAMPS: { match: RegExp; presets: NumberPreset[] }[] = [
  {
    // Type sizes a reader actually distinguishes, not a linear ramp.
    match: /(^|_)font_size$/,
    presets: [
      { value: 14, label: 'S' },
      { value: 16, label: 'M' },
      { value: 20, label: 'L' },
      { value: 28, label: 'XL' },
    ],
  },
  {
    // A composite's own headline size — bigger than body copy at every step, so
    // it cannot share the `font_size` ramp.
    match: /(^|_)heading_size$/,
    presets: [
      { value: 20, label: 'S' },
      { value: 24, label: 'M' },
      { value: 32, label: 'L' },
      { value: 40, label: 'XL' },
    ],
  },
  {
    match: /(^|_)line_height$/,
    presets: [
      { value: 20, label: 'Tight' },
      { value: 25, label: 'Normal' },
      { value: 30, label: 'Relaxed' },
      { value: 36, label: 'Loose' },
    ],
  },
  {
    // Before the generic `…spacing` ramp, which `letter_spacing` also matches —
    // and 8px of letter spacing is not a typographic choice, it is a mistake.
    match: /(^|_)letter_spacing$/,
    presets: [
      { value: 0, label: 'None' },
      { value: 0.5, label: 'S' },
      { value: 1, label: 'M' },
      { value: 2, label: 'L' },
    ],
  },
  {
    match: /(^|_)(padding|margin|spacing|gap)$/,
    presets: [
      { value: 0, label: 'None' },
      { value: 8, label: 'S' },
      { value: 16, label: 'M' },
      { value: 32, label: 'L' },
    ],
  },
  {
    // A hairline, a normal rule, and two emphatic ones.
    match: /(^|_)(border_width|thickness)$/,
    presets: [
      { value: 0, label: 'None' },
      { value: 1, label: 'S' },
      { value: 2, label: 'M' },
      { value: 4, label: 'L' },
    ],
  },
  {
    match: /(^|_)border_radius$/,
    presets: [
      { value: 0, label: 'Square' },
      { value: 4, label: 'S' },
      { value: 8, label: 'M' },
      { value: 999, label: 'Pill' },
    ],
  },
  {
    // A spacer's whole job is its height.
    match: /(^|_)height$/,
    presets: [
      { value: 8, label: 'S' },
      { value: 24, label: 'M' },
      { value: 48, label: 'L' },
      { value: 80, label: 'XL' },
    ],
  },
  {
    match: /(^|_)indent$/,
    presets: [
      { value: 0, label: 'None' },
      { value: 12, label: 'S' },
      { value: 20, label: 'M' },
      { value: 32, label: 'L' },
    ],
  },
  {
    // A follow-row badge. Below 20px a logo is unreadable, above 40px it is a
    // picture — the useful range is narrow, which is what makes a ramp right.
    match: /(^|_)icon_size$/,
    presets: [
      { value: 20, label: 'S' },
      { value: 24, label: 'M' },
      { value: 32, label: 'L' },
      { value: 40, label: 'XL' },
    ],
  },
  {
    // A masthead wordmark, not a content image: 240px is already half the column.
    match: /(^|_)logo_width$/,
    presets: [
      { value: 120, label: 'S' },
      { value: 160, label: 'M' },
      { value: 200, label: 'L' },
      { value: 240, label: 'XL' },
    ],
  },
  {
    // A divider's length is a **percentage** of the column — a pixel rule would
    // be the wrong length the moment the column narrows on a phone.
    match: /(^|_)line_width$/,
    presets: [
      { value: 25, label: '25%' },
      { value: 50, label: '50%' },
      { value: 75, label: '75%' },
      { value: 100, label: '100%' },
    ],
  },
  {
    // Email body widths in the wild — the container's `max_width`.
    match: /(^|_)max_width$/,
    presets: [
      { value: 480, label: null },
      { value: 600, label: null },
      { value: 680, label: null },
      { value: 760, label: null },
    ],
  },
  {
    /**
     * A component's own `width`, which is nearly always an image's.
     *
     * It used to share the container ramp (480–760), which offered nothing below
     * half the content column — so an inline icon or a 150px logo was not
     * reachable through the inspector at all, only through the Source view. The
     * ramp now spans thumbnail to full-bleed, and the exact-value entry beside it
     * covers whatever falls between.
     */
    match: /(^|_)width$/,
    presets: [
      { value: 120, label: null },
      { value: 200, label: null },
      { value: 320, label: null },
      { value: 600, label: null },
    ],
  },
];

/** The ramp for a param, or null when it has no sensible one (an arbitrary count). */
export function numberPresets(paramName: string): NumberPreset[] | null {
  const name = paramName.toLowerCase();
  for (const ramp of RAMPS) {
    if (ramp.match.test(name)) return ramp.presets;
  }
  return null;
}

/**
 * The options to render, with the current value included when it is off-ramp.
 *
 * The extra option is what makes the control lossless: a template holding
 * `font_size: 17` shows `17` selected next to S/M/L/XL, so selecting a preset is a
 * choice rather than something that already happened.
 */
export function numberOptions(
  paramName: string,
  current: unknown,
): NumberPreset[] | null {
  const presets = numberPresets(paramName);
  if (!presets) return null;
  if (typeof current !== 'number') return presets;
  if (presets.some((preset) => preset.value === current)) return presets;

  // Inserted in order, so the ramp still reads left-to-right as "bigger".
  const extended = [...presets, { value: current, label: null }];
  return extended.sort((a, b) => a.value - b.value);
}

/** What a preset shows: its name, or the number when it has none. */
export function presetLabel(preset: NumberPreset): string {
  return preset.label ?? String(preset.value);
}
